"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { InitialData } from "@/lib/interface/InitialData.interface";
import { Timeframe } from "@/lib/enums/timeframe.enum";

interface UseMarketSelectionReturn {
  pairs: string[];
  timeframes: string[];
  exchanges: string[];
  selectedPair: string;
  selectedTimeframe: Timeframe;
  selectedExchange: string;
  setSelectedPair: (pair: string) => void;
  setSelectedTimeframe: (timeframe: Timeframe) => void;
  setSelectedExchange: (exchange: string) => void;
  initializeSelection: (data: InitialData) => void;
}

export const useMarketSelection = (): UseMarketSelectionReturn => {
  const STORAGE_KEY = "primus:view";

  const readStoredView = useCallback(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<{
        pair: string;
        exchange: string;
        timeframe: string;
      }>;

      const pair = typeof parsed.pair === "string" ? parsed.pair : "";
      const exchange =
        typeof parsed.exchange === "string" ? parsed.exchange : "";
      const timeframeRaw =
        typeof parsed.timeframe === "string" ? parsed.timeframe : undefined;
      const timeframe = timeframeRaw ? (timeframeRaw as Timeframe) : undefined;
      const validTimeframe =
        !!timeframe && Object.values(Timeframe).includes(timeframe);

      return {
        pair,
        exchange,
        timeframe: validTimeframe ? timeframe : undefined,
      };
    } catch {
      return null;
    }
  }, []);

  const writeStoredView = useCallback(
    (
      next: Partial<{ pair: string; exchange: string; timeframe: Timeframe }>
    ) => {
      if (typeof window === "undefined") return;
      try {
        const current = readStoredView() ?? {
          pair: "",
          exchange: "",
          timeframe: undefined as Timeframe | undefined,
        };
        const merged = {
          pair: typeof next.pair === "string" ? next.pair : current.pair,
          exchange:
            typeof next.exchange === "string"
              ? next.exchange
              : current.exchange,
          timeframe: next.timeframe ?? current.timeframe,
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      } catch {
        // ignore
      }
    },
    [readStoredView]
  );

  const [pairs, setPairs] = useState<string[]>([]);
  const [timeframes, setTimeframes] = useState<string[]>([]);
  const [exchanges, setExchanges] = useState<string[]>([]);

  const storedView = readStoredView();
  const [selectedPair, setSelectedPairState] = useState<string>(
    storedView?.pair ?? ""
  );
  const [selectedTimeframe, setSelectedTimeframeState] = useState<Timeframe>(
    storedView?.timeframe ?? Timeframe["1d"]
  );
  const [selectedExchange, setSelectedExchangeState] = useState<string>(
    storedView?.exchange ?? ""
  );

  const hasHydratedRef = useRef(false);
  const selectionRef = useRef({
    pair: selectedPair,
    timeframe: selectedTimeframe,
    exchange: selectedExchange,
  });

  useEffect(() => {
    selectionRef.current = {
      pair: selectedPair,
      timeframe: selectedTimeframe,
      exchange: selectedExchange,
    };
  }, [selectedPair, selectedTimeframe, selectedExchange]);

  const setSelectedPair = useCallback(
    (pair: string) => {
      setSelectedPairState(pair);
      writeStoredView({ pair });
    },
    [writeStoredView]
  );

  const setSelectedTimeframe = useCallback(
    (timeframe: Timeframe) => {
      setSelectedTimeframeState(timeframe);
      writeStoredView({ timeframe });
    },
    [writeStoredView]
  );

  const setSelectedExchange = useCallback(
    (exchange: string) => {
      setSelectedExchangeState(exchange);
      writeStoredView({ exchange });
    },
    [writeStoredView]
  );

  const initializeSelection = useCallback(
    (data: InitialData) => {
      setPairs(data.pairs || []);
      setTimeframes(data.timeframes || []);
      setExchanges(data.exchanges || []);

      const shouldApplyDefaults = !hasHydratedRef.current;

      if (shouldApplyDefaults) {
        const current = selectionRef.current;

        if (!current.pair && data.defaultPair) {
          setSelectedPair(data.defaultPair);
        }
        if (
          (!current.timeframe ||
            !Object.values(Timeframe).includes(current.timeframe)) &&
          data.defaultTimeframe
        ) {
          setSelectedTimeframe(data.defaultTimeframe as Timeframe);
        }
        if (!current.exchange && data.defaultExchange) {
          setSelectedExchange(data.defaultExchange);
        }

        hasHydratedRef.current = true;
      }
    },
    [setSelectedExchange, setSelectedPair, setSelectedTimeframe]
  );

  return {
    pairs,
    timeframes,
    exchanges,
    selectedPair,
    selectedTimeframe,
    selectedExchange,
    setSelectedPair,
    setSelectedTimeframe,
    setSelectedExchange,
    initializeSelection,
  };
};
